import React, { useState } from 'react';
import Box from '@components/Atoms/Box';
// MUI's Box forwards refs (required for Tooltip anchoring); the local Box
// atom is a plain function component and cannot anchor a Tooltip.
import { Tooltip, Box as MuiBox } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PMScores, PMBandDef, PMOrganizationNode, PMTeacherNode, PMSectionNode, PMStudentNode } from './types';
import { LeafColumn, CellValue } from './columns';

const BAND_COLORS: Record<string, string> = {
  'band-0': 'success.main',
  'band-1': 'secondary.dark',
  'band-2': 'error.main',
};

// Best band → green, worst → red, anything between → amber; works for any
// configured band count.
const bandColor = (pct: number | null, bands: PMBandDef[], id: string) => {
  if (pct === null || pct === undefined) {
    return 'text.disabled';
  }
  if (!bands.length) {
    return 'text.primary';
  }

  const byId = BAND_COLORS[id.toLowerCase()];
  if (byId) {
    return byId;
  }

  const index = bands.findIndex((band) => pct >= band.lower);
  const effective = index === -1 ? bands.length - 1 : index;
  if (effective === 0) {
    return 'success.main';
  }
  if (effective === bands.length - 1) {
    return 'error.main';
  }
  return 'secondary.dark';
};

const dividerSx = { borderLeft: 2, borderColor: 'grey.300' };

const Cell: React.FC<{ id: string; cell: CellValue; bands: PMBandDef[]; divider?: boolean }> = ({ id, cell, bands, divider = false }) => {
  let content: React.ReactNode;
  if (cell.value === null || cell.value === undefined) {
    content = (
      <Box component="span" sx={{ color: 'text.disabled' }}>
        —
      </Box>
    );
  } else if (cell.kind === 'pct') {
    content = (
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        <Box component="span" sx={{ fontWeight: 600, color: bandColor(cell.value, bands, id) }}>
          {cell.value}%
        </Box>
        {cell.counts && (
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            {` (${cell.counts})`}
          </Box>
        )}
      </Box>
    );
  } else if (cell.kind === 'growth') {
    content = (
      <Box component="span" sx={{ fontWeight: 600, color: cell.value >= 0 ? 'success.dark' : 'error.dark' }}>
        {cell.value > 0 ? '+' : ''}
        {cell.value}
      </Box>
    );
  } else {
    content = (
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {cell.value}
      </Box>
    );
  }
  const cellSx = {
    textAlign: 'center' as const,
    fontSize: '0.8125rem',
    alignSelf: 'stretch' as const,
    ...(divider ? dividerSx : {}),
  };
  if (cell.tooltip) {
    return (
      <Tooltip title={cell.tooltip} arrow>
        <MuiBox sx={cellSx}>{content}</MuiBox>
      </Tooltip>
    );
  }
  return <Box sx={cellSx}>{content}</Box>;
};

interface SpanCell {
  label: string;
  span: number;
  tooltip?: string;
  divider?: boolean;
}

const spanRow = (
  leaves: LeafColumn[],
  pick: (leaf: LeafColumn) => string,
  tooltipPick?: (leaf: LeafColumn) => string | undefined,
): SpanCell[] => {
  const cells: SpanCell[] = [];
  leaves.forEach((leaf, index) => {
    const label = pick(leaf);
    const prev = cells[cells.length - 1];
    const prevLeaf = index > 0 ? leaves[index - 1] : null;
    // Merge consecutive identical labels, but only within the same group so
    // identical labels in different groups don't fuse
    if (prev && prevLeaf && pick(prevLeaf) === label && prevLeaf.group === leaf.group) {
      prev.span += 1;
    } else {
      cells.push({
        label,
        span: 1,
        tooltip: tooltipPick?.(leaf),
        divider: !prevLeaf || prevLeaf.group !== leaf.group,
      });
    }
  });
  return cells;
};

interface LevelProps {
  leaves: LeafColumn[];
  bands: PMBandDef[];
  dividers: Set<string>;
}

interface RowProps extends LevelProps {
  name: string;
  scores: PMScores;
  depth: number;
  isExpandable: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  emphasize?: boolean;
}

const NAME_COL = 'minmax(15rem, 1.6fr)';
// Detail tabs show "23.4% (7/10)" inline — need wider columns than Summary.
const hasInlineCounts = (leaves: LeafColumn[]) => leaves.some((leaf) => leaf.stat === '% of Questions' || leaf.stat === 'Performance %');
const colMinRem = (leaves: LeafColumn[]) => (hasInlineCounts(leaves) ? 6.5 : 4.75);
const leafWidthRem = (leaves: LeafColumn[]) => (hasInlineCounts(leaves) ? 7 : 5.5);
const gridTemplate = (leafCount: number, minRem: number) =>
  `${NAME_COL} ${Array.from({ length: leafCount })
    .map(() => `minmax(${minRem}rem, 1fr)`)
    .join(' ')}`;

const OutcomeRow: React.FC<RowProps> = ({
  name,
  scores,
  leaves,
  bands,
  dividers,
  depth,
  isExpandable,
  isExpanded = false,
  onToggle,
  emphasize = false,
}) => (
  <Box
    onClick={isExpandable ? onToggle : undefined}
    data-testid="ct/pm-outcome-row"
    sx={{
      display: 'grid',
      gridTemplateColumns: gridTemplate(leaves.length, colMinRem(leaves)),
      gap: 0.5,
      alignItems: 'center',
      px: 2,
      py: 0.75,
      borderTop: 1,
      borderColor: 'divider',
      cursor: isExpandable ? 'pointer' : 'default',
      bgcolor: emphasize ? 'action.selected' : 'transparent',
      '&:hover': isExpandable ? { bgcolor: 'action.hover' } : undefined,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        pl: depth * 2.5,
        // Keep the level/name column visible during horizontal scroll
        position: 'sticky',
        left: 0,
        zIndex: 1,
        bgcolor: emphasize ? 'grey.100' : 'background.paper',
        alignSelf: 'stretch',
        mr: -0.5,
        pr: 0.5,
      }}
    >
      {isExpandable ? (
        <ExpandMoreIcon
          sx={{
            fontSize: '1rem',
            flexShrink: 0,
            transition: 'transform 0.15s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(-90deg)',
          }}
        />
      ) : (
        <Box sx={{ width: '1rem', flexShrink: 0 }} />
      )}
      <Box
        component="span"
        sx={{
          fontSize: '0.8125rem',
          fontWeight: emphasize ? 700 : 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </Box>
    </Box>
    {leaves.map((leaf) => (
      <Cell key={leaf.id} id={leaf.id} cell={leaf.render(scores)} bands={bands} divider={dividers.has(leaf.id)} />
    ))}
  </Box>
);

const StudentRows: React.FC<LevelProps & { students: PMStudentNode[] }> = ({ students, ...levelProps }) => (
  <>
    {students.map((student) => (
      <OutcomeRow key={student.student_id} name={student.student_name} scores={student} depth={4} isExpandable={false} {...levelProps} />
    ))}
  </>
);

const SectionRow: React.FC<LevelProps & { section: PMSectionNode }> = ({ section, ...levelProps }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <OutcomeRow
        name={section.section_name}
        scores={section}
        depth={3}
        isExpandable={section.students.length > 0}
        isExpanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        {...levelProps}
      />
      {expanded && <StudentRows students={section.students} {...levelProps} />}
    </>
  );
};

const TeacherRow: React.FC<LevelProps & { teacher: PMTeacherNode }> = ({ teacher, ...levelProps }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <OutcomeRow
        name={teacher.teacher_name}
        scores={teacher}
        depth={2}
        isExpandable={teacher.sections.length > 0}
        isExpanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        {...levelProps}
      />
      {expanded && teacher.sections.map((section) => <SectionRow key={section.section_id} section={section} {...levelProps} />)}
    </>
  );
};

const OrganizationRow: React.FC<LevelProps & { organization: PMOrganizationNode }> = ({ organization, ...levelProps }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <OutcomeRow
        name={organization.org_name}
        scores={organization}
        depth={1}
        isExpandable={organization.teachers.length > 0}
        isExpanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        {...levelProps}
      />
      {expanded &&
        organization.teachers.map((teacher) => (
          <TeacherRow key={`${teacher.teacher_id ?? teacher.teacher_name}`} teacher={teacher} {...levelProps} />
        ))}
    </>
  );
};

const headerCellSx = {
  textAlign: 'center' as const,
  fontSize: '0.6875rem',
  fontWeight: 700,
  color: 'text.secondary',
  // Allow wrapping (up to 3 lines) instead of lengthwise truncation
  overflow: 'hidden',
  whiteSpace: 'normal' as const,
  wordBreak: 'break-word' as const,
  lineHeight: 1.25,
  px: 0.25,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const HeaderSpanRow: React.FC<{
  cells: SpanCell[];
  leftLabel?: string;
  leafCount: number;
  colMin: number;
  emphasizeGroups?: boolean;
}> = ({ cells, leftLabel = '', leafCount, colMin, emphasizeGroups = false }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: gridTemplate(leafCount, colMin),
      gap: 0.5,
      px: 2,
      py: 0.5,
      bgcolor: emphasizeGroups ? 'grey.200' : 'grey.100',
    }}
  >
    <Box
      sx={{
        ...headerCellSx,
        textAlign: 'left',
        textTransform: 'uppercase',
        position: 'sticky',
        left: 0,
        zIndex: 1,
        bgcolor: emphasizeGroups ? 'grey.200' : 'grey.100',
        alignSelf: 'stretch',
        mr: -0.5,
        pr: 0.5,
      }}
    >
      {leftLabel}
    </Box>
    {cells.map((cell, index) => {
      const cellSx = {
        ...headerCellSx,
        gridColumn: `span ${cell.span}`,
        ...(emphasizeGroups && cell.label ? { borderBottom: 2, borderColor: 'divider' } : {}),
        ...(cell.divider ? { borderLeft: 2, borderColor: 'grey.400', alignSelf: 'stretch' } : {}),
      };
      // Group labels can span dozens of columns; pin the text to the
      // visible left edge of the span while scrolling so the active group
      // name never sits off-screen
      const label =
        emphasizeGroups && cell.span > 4 ? (
          <Box
            component="span"
            sx={{
              position: 'sticky',
              left: 8,
              display: 'inline-block',
              width: 'max-content',
              maxWidth: '100%',
            }}
          >
            {cell.label}
          </Box>
        ) : (
          cell.label
        );
      const groupSx = emphasizeGroups && cell.span > 4 ? { textAlign: 'left' as const } : {};
      return cell.tooltip ? (
        <Tooltip key={`${cell.label}-${index}`} title={cell.tooltip} arrow>
          <MuiBox sx={{ ...cellSx, ...groupSx, cursor: 'help' }}>{label}</MuiBox>
        </Tooltip>
      ) : (
        <Box key={`${cell.label}-${index}`} sx={{ ...cellSx, ...groupSx }}>
          {label}
        </Box>
      );
    })}
  </Box>
);

export interface HierarchyData {
  district: PMScores;
  organizations: PMOrganizationNode[];
}

const PMOutcomeHierarchy: React.FC<{
  data: HierarchyData;
  districtName: string;
  bands: PMBandDef[];
  leaves: LeafColumn[];
}> = ({ data, districtName, bands, leaves }) => {
  const [expanded, setExpanded] = useState(true);
  // Leaves that start a new column group get a vertical divider, drawn
  // through the header and every body row so wide groups stay readable
  const dividers = new Set(leaves.filter((leaf, index) => index === 0 || leaves[index - 1].group !== leaf.group).map((leaf) => leaf.id));
  const levelProps: LevelProps = { leaves, bands, dividers };

  const groupCells = spanRow(leaves, (leaf) => leaf.group);
  const columnCells = spanRow(
    leaves,
    (leaf) => leaf.column,
    (leaf) => leaf.columnTooltip,
  );
  const hasStats = leaves.some((leaf) => leaf.stat);
  const colMin = colMinRem(leaves);

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
      <Box sx={{ minWidth: `${16 + leaves.length * leafWidthRem(leaves)}rem` }}>
        <HeaderSpanRow cells={groupCells} leafCount={leaves.length} colMin={colMin} emphasizeGroups />
        <HeaderSpanRow cells={columnCells} leftLabel="Level" leafCount={leaves.length} colMin={colMin} />
        {hasStats && (
          <HeaderSpanRow
            cells={leaves.map((leaf) => ({
              label: leaf.stat,
              span: 1,
              divider: dividers.has(leaf.id),
            }))}
            leafCount={leaves.length}
            colMin={colMin}
          />
        )}

        <OutcomeRow
          name={districtName}
          scores={data.district}
          depth={0}
          isExpandable={data.organizations.length > 0}
          isExpanded={expanded}
          onToggle={() => setExpanded(!expanded)}
          emphasize
          {...levelProps}
        />
        {expanded &&
          data.organizations.map((organization) => (
            <OrganizationRow key={organization.org_id} organization={organization} {...levelProps} />
          ))}
      </Box>
    </Box>
  );
};

export default PMOutcomeHierarchy;
